import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Modal, Card, Table as AntTable } from 'antd';
import DefaultLayout from 'components/DefaultLayout';
import dayjs from 'dayjs';
import moment, { Moment } from 'moment';
import { EyeOutlined } from '@ant-design/icons';
import { QTable } from 'ui';
import { useT } from 'hooks/Locale/useT';
import { Filter } from 'components/Modals';
import {
  DEFAULT_PERIOD_DAYS,
  auditOwnerFilter,
  auditOrganizationFilter,
  auditCustomerFilter,
  auditCoreFilters,
} from './filter';
import './styles.scss';

const mapStateToProps = (state: any) => ({
  session: state.session,
  organization: state.session.organization,
});

type Props = ReturnType<typeof mapStateToProps>;

const defaultPeriod = (): [Moment, Moment] => [
  moment().subtract(DEFAULT_PERIOD_DAYS - 1, 'days').startOf('day'),
  moment().endOf('day'),
];

const Audit: React.FC<Props> = ({ session, organization }) => {
  const t = useT();

  const [filter, setFilter] = useState<Record<string, any>>({ period: defaultPeriod() });
  const [reload, setReload] = useState(false);

  const [isLogDetailsVisible, setIsLogDetailsVisible] = useState(false);
  const [logDetailsContent, setLogDetailsContent] = useState<any>({});
  const [logDetailsModalTitle, setLogDetailsModalTitle] = useState('');

  const isSuper = session.active_user_type === 'SUPER_ADMIN';
  const isCustomAdmin =
    session.active_user_type === 'CUSTOMER_ADMIN' ||
    session.active_user_type === 'CUSTOMER';
  const isResellerLike =
    session.active_user_type === 'DISTRIBUTOR_ADMIN' ||
    session.active_user_type === 'RESELLER_ADMIN' ||
    session.active_user_type === 'ORGANIZATION';

  useEffect(() => {
    if (isCustomAdmin && filter?.filterBy !== 'CUSTOMER') {
      setFilter(f => ({ ...f, filterBy: 'CUSTOMER' }));
    }
  }, [isCustomAdmin]);

  const effectiveFilterBy: 'CUSTOMER' | 'ORGANIZATION' =
      isCustomAdmin ? 'CUSTOMER' : filter?.filterBy === 'CUSTOMER' ? 'CUSTOMER' : 'ORGANIZATION';

  let scope: 'GLOBAL' | 'ORGANIZATION' | 'CUSTOMER' = 'GLOBAL';
  let uuid: string | number | undefined;

  if (isSuper) {
    if (effectiveFilterBy === 'CUSTOMER') {
      uuid = filter?.customer;
      scope = uuid ? 'CUSTOMER' : 'GLOBAL';
    } else {
      uuid = filter?.organization;
      scope = uuid ? 'ORGANIZATION' : 'GLOBAL';
    }
  } else if (isResellerLike) {
    if (effectiveFilterBy === 'CUSTOMER' && filter?.customer) {
      scope = 'CUSTOMER';
      uuid = filter.customer;
    } else {
      scope = 'ORGANIZATION';
      uuid = organization?.organization_uuid ?? organization?.id;
    }
  } else {
    scope = 'GLOBAL';
    uuid = undefined;
  }

  const withQuery = (endpoint: string, q: Record<string, unknown>) => {
    const qs = new URLSearchParams(
      Object.entries(q).filter(([, v]) => v !== undefined && v !== null) as [string, string][]
    ).toString();

    return qs ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}${qs}` : endpoint;
  };

  const canChooseDimension =
    session.active_user_type === 'SUPER_ADMIN' ||
    session.active_user_type === 'DISTRIBUTOR_ADMIN' ||
    session.active_user_type === 'RESELLER_ADMIN';

  const isCustomer = effectiveFilterBy === 'CUSTOMER';
  const customerId = isCustomer ? (filter?.customer ?? session.customer_id) : undefined;
  const organizationId = !isCustomer ? (filter?.organization ?? organization?.organization_uuid ?? organization?.id) : undefined;

  const modules = (() => {
    type ModuleItem = { endpoint?: string; name?: string; label?: string; type?: string; [k: string]: any };

    const owner = (canChooseDimension ? (auditOwnerFilter as ModuleItem[]) : []);
    const dimensionFilters = (isCustomer ? (auditCustomerFilter as ModuleItem[]) : (isSuper ? (auditOrganizationFilter as ModuleItem[]) : []));
    const dimMapped = dimensionFilters.map((m: ModuleItem) => ({
      ...m,
      endpoint: withQuery(m.endpoint as string, {
        filterBy: effectiveFilterBy,
        _sig: `${isCustomer ? 'C' : 'O'}|${customerId ?? ''}|${organizationId ?? ''}`,
      }),
    }));
    const coreFilters = (auditCoreFilters as ModuleItem[]).map((m: ModuleItem) => {
      if (m.name === 'user_uuid' || m.name === 'object_name' || m.name === 'log_name') {
        return {
          ...m,
          endpoint: withQuery(m.endpoint as string, {
            scope,
            uuid,
            _sig: `${scope}|${uuid ?? ''}`,
          }),
        } as ModuleItem;
      }

      return m;
    });
    const combined = [
      ...owner,
      ...dimMapped,
      ...coreFilters,
    ];

    return combined.map(m => ({
      ...m,
      label: m.label ?? m.name ?? '',
      type: m.type ?? 'text',
    })) as any;
  })();

  const columns = [
    {
      title: t('system.audit.datetime'),
      key: 'created_at',
      render: (_: string, record: any) =>
          dayjs(record.created_at).format('DD.MM.YYYY HH:mm:ss'),
    },
    {
      title: t('system.audit.log_name'),
      key: 'log_name',
      dataIndex: 'log_name',
      render: (logName: string) => t(`system.audit.object_type.${logName}`, logName),
    },
    {
      title: t('system.audit.caused_by'),
      key: 'caused_by',
      render: (_: any, record: any) => renderCauser(record),
    },
    {
      title: t('system.audit.action_type'),
      key: 'event',
      dataIndex: 'description',
    },
    {
      title: t('system.audit.object_name'),
      key: 'object_name',
      render: (_: string, record: any) => renderObjectName(record),
    },
    {
      title: t('system.audit.attributes'),
      key: 'attributes',
      render: (_: string, record: any) => (
        <p
          className="description-icon"
          onClick={() => {
            setIsLogDetailsVisible(true);
            setLogDetailsModalTitle(t('system.audit.log_details_modal_title'));
            setLogDetailsContent({
              country: record.country,
              ip: record.client_ip,
              properties: record.properties,
              os: record.client_os,
              browser: record.client_browser,
            });
          }}
          style={{ cursor: 'pointer' }}
        >
          <FormattedMessage id="general.show" /> <EyeOutlined />
        </p>
      ),
    },
  ];

  const formatEventProperties = (data: any) => {
    if (!data)  {
      return null;
    }

    const { properties, ...rest } = data;
    const rows = Object.entries(rest).map(([key, value]) => ({ key, value }));

    return (
      <AntTable
        columns={[
          { title: t('system.audit.key'), dataIndex: 'key', align: 'right' as const, render: (_: any, r: any) => <b>{r.key}</b> },
          { title: t('system.audit.value'), dataIndex: 'value' },
        ]}
        dataSource={rows}
        style={{ marginTop: 20 }}
        showHeader={false}
        pagination={{ hideOnSinglePage: true }}
        size="small"
        className="event-properties-table"
        rowKey="key"
      />
    );
  };

  const formatProperties = (json: any) => {
    if (!json || typeof json !== 'object') {
      return null;
    }

    if (json.error) {
      return (
        <Card title={t('general.error')} bordered={false}>
          <p>{json.error}</p>
        </Card>
      );
    }

    if (json.attributes) {
      const attrs = json.attributes;
      // Booleans (custom SWITCH fields) render as empty cells in React unless stringified.
      const show = (v: any) => {
        if (typeof v === 'boolean') return String(v);
        if (typeof v === 'object' && v !== null) return <pre>{JSON.stringify(v, null, 2)}</pre>;
        return v ?? '';
      };
      // lecture_sessions.lectors holds user_types ids; the list API resolves them to names.
      const showField = (k: string, v: any) =>
        k === 'lectors' && Array.isArray(v)
          ? v.map((id: number | string) => lectorNames[String(id)] ?? `#${id}`).join(', ') || '-'
          : show(v);
      const data = Object.keys(attrs).map(k => ({
        field: k,
        new: showField(k, attrs[k]),
        old: showField(k, json.old?.[k]),
      }));
      return (
        <AntTable
          columns={[
            { title: t('general.field'), dataIndex: 'field', render: (_: any, r: any) => <b>{r.field}</b> },
            { title: t('system.audit.new'), dataIndex: 'new' },
            { title: t('system.audit.old'), dataIndex: 'old' },
          ]}
          dataSource={data}
          style={{ marginTop: 20, maxWidth: '100%' }}
          pagination={false}
          rowKey="field"
        />
      );
    }

    return null;
  };

  const [lectorNames, setLectorNames] = useState<Record<string, string>>({});
  const [usersByTypeUuid, setUsersByTypeUuid] =
      useState<Record<string, {name?: string; surname?: string} | string>>({});

  const renderCauser = (record: any) => {
    const u = usersByTypeUuid?.[record.user_type_uuid];
    if (u && typeof u === 'object') {
      const full = [u.name, u.surname].filter(Boolean).join(' ');
      if (full) return full;
    }
    if (typeof u === 'string' && u.trim()) return u;

    const systemProcess = record.properties?.system_process;
    if (systemProcess) {
      return `${t('system.audit.system_process')}: ${systemProcess}`;
    }

    return record.user_type_uuid ?? '-';
  };

  const renderObjectName = (record: any) => {
    return record.object_name;
  };

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageHeader title={t('system.audit.title')} />

      <DefaultLayout.PageContent>
        <Filter
          initialValues={{ period: filter.period }}
          setFilter={setFilter}
          resetFields={{
            filterBy: ['organization', 'customer', 'object_name', 'log_name', 'user_uuid'],
            organization: ['customer', 'object_name', 'log_name', 'user_uuid'],
            customer: ['organization', 'object_name', 'log_name', 'user_uuid'],
           }}
          modules={modules}
          submit={() => setReload(true)}
          hasSearch={false}
        />

        <QTable
          tableKey="auditLogs"
          columns={columns}
          url="/api/v1/audit/logs"
          setReload={setReload}
          rowSelection
          reload={reload}
          rowKey="id"
          filter={{ ...filter, scope, uuid }}
          showTotalResults
          onLoadFunction={(resp: any) => {
            setUsersByTypeUuid(resp?.users || {});
            setLectorNames(resp?.lectors || {});
          }}
        />

        <Modal
          title={logDetailsModalTitle}
          open={isLogDetailsVisible}
          width="75%"
          onCancel={() => setIsLogDetailsVisible(false)}
          onOk={() => setIsLogDetailsVisible(false)}
          cancelButtonProps={{ style: { display: 'none' } }}
          okButtonProps={{ disabled: true, style: { display: 'none' } }}
        >
          {formatEventProperties(logDetailsContent)}
          {formatProperties(logDetailsContent?.properties)}
        </Modal>
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};

export default connect(mapStateToProps)(Audit);
